import { useState } from "react";
import { api } from "@/lib/api";
import { Phone, CheckCircle2, X, Loader2, Clock } from "lucide-react";

/**
 * Lead-capture CTA – shown on Step 1 of the claim wizard for anonymous users
 * (and as a soft secondary option for everyone). Captures name + mobile and
 * opens a "we'll call you in 5 minutes" promise.
 */
export default function LeadCaptureCTA({ source = "claim_wizard_step1", compact = false }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (mobile.length < 10) {
      setError("Please enter a valid 10-digit mobile");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/leads", { name: name || null, mobile, source });
      setSuccess(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <div className={`relative rounded-2xl border border-dashed border-[#FF7A00]/50 bg-gradient-to-br from-[#FFF8F0] to-[#FFF2E5] p-4 sm:p-5 ${compact ? "" : "mb-6"}`} data-testid="lead-capture-card">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#FF7A00] text-white flex items-center justify-center shrink-0 animate-pulse-ring">
              <Phone className="w-5 h-5" />
            </div>
            <div>
              <div className="font-heading font-bold text-slate-900 text-sm sm:text-base">Short on time? We'll call you back.</div>
              <div className="text-xs sm:text-sm text-slate-600 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-[#FF7A00]" />
                Save your mobile — our claim expert calls within 5 minutes.
              </div>
            </div>
          </div>
          <button
            onClick={() => setOpen(true)}
            className="ml-auto bg-[#FF7A00] hover:bg-[#CC6200] text-white rounded-xl px-5 py-2.5 font-semibold text-sm whitespace-nowrap transition-colors"
            data-testid="lead-capture-open"
          >
            Save & Call Me
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative rounded-2xl border border-[#FF7A00] bg-white p-5 sm:p-6 shadow-lg mb-6" data-testid="lead-capture-form">
      <button
        onClick={() => setOpen(false)}
        className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"
        aria-label="Close"
        data-testid="lead-capture-close"
      >
        <X className="w-4 h-4" />
      </button>
      {success ? (
        <div className="text-center py-2" data-testid="lead-capture-success">
          <CheckCircle2 className="w-12 h-12 text-[#00A86B] mx-auto mb-2" />
          <h3 className="font-heading font-bold text-lg text-slate-900">Got it! We're calling you shortly.</h3>
          <p className="text-sm text-slate-600 mt-1">{success.message}</p>
          <div className="mt-3 inline-block px-3 py-1.5 rounded-lg bg-slate-50 font-mono text-xs font-bold text-slate-700" data-testid="lead-id">
            {success.lead_id}
          </div>
        </div>
      ) : (
        <form onSubmit={submit}>
          <div className="flex items-center gap-2 mb-3">
            <Phone className="w-5 h-5 text-[#FF7A00]" />
            <div className="font-heading font-bold text-slate-900">Drop your mobile — we'll call back in 5 minutes</div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name (optional)"
              className="bg-white border border-slate-300 rounded-xl px-4 py-3 text-slate-900 focus:ring-2 focus:ring-[#FF7A00] focus:border-transparent outline-none"
              data-testid="lead-name"
            />
            <input
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
              maxLength={10}
              placeholder="10-digit mobile *"
              required
              className="bg-white border border-slate-300 rounded-xl px-4 py-3 text-slate-900 focus:ring-2 focus:ring-[#FF7A00] focus:border-transparent outline-none"
              data-testid="lead-mobile"
            />
          </div>
          {error && <div className="mt-2 text-xs text-red-600" data-testid="lead-error">{error}</div>}
          <button
            type="submit"
            disabled={submitting || mobile.length < 10}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#FF7A00] hover:bg-[#CC6200] disabled:bg-slate-300 text-white rounded-xl px-5 py-3 font-semibold transition-colors"
            data-testid="lead-submit"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Yes, call me back <Phone className="w-4 h-4" /></>}
          </button>
          <p className="mt-3 text-[11px] text-center text-slate-500">No spam. No automated calls. A real human, in 5 minutes.</p>
        </form>
      )}
    </div>
  );
}
